import React, { useMemo } from "react";
import { XAxis, YAxis, CartesianGrid, Tooltip, AreaChart, Area, ResponsiveContainer, ReferenceLine } from "recharts";
import { Loader } from "@shared/components";
import { PatientAvgSensorDataChartAvgDotValue } from "@containers/Patient/interfaces";
import { getShortDateFormat } from "@shared/utils";
import { DATE_FORMAT } from "@shared/constants";

import { ArtifactLogicDot, CustomTooltip } from "./components";

import "./index.scss";

export interface SensorsChartProps {
  loading?: boolean;
  domain?: [number, number];
  sensorsSignalType: string;
  referenceLineYValue?: number | null;
  values: PatientAvgSensorDataChartAvgDotValue[];
  onDotClick?: (dot: PatientAvgSensorDataChartAvgDotValue) => void;
}

const yTickFormatter = (value: number) => Math.floor(value).toString();

const SensorsAreaChart: React.FC<SensorsChartProps> = (props) => {
  const { referenceLineYValue, values, sensorsSignalType, domain, onDotClick, loading } = props;

  const data = useMemo(() => {
    if (values.length || !domain) return values;

    return [
      { xValue: 0, yValue: getShortDateFormat(new Date(domain[0]), DATE_FORMAT.CHART) },
      { xValue: 0, yValue: getShortDateFormat(new Date(domain[1]), DATE_FORMAT.CHART) },
    ];
  }, [values, domain]);

  const yDomain: [number, number] = useMemo(() => {
    const allValues = values.map((d) => d.xValue).filter((v): v is number => v !== null && v !== undefined);

    if (!allValues.length) {
      return [0, 100];
    }

    const maxValue = Math.max(...allValues);
    const buffer = referenceLineYValue || Math.ceil(maxValue * 0.1) || 10;

    return [0, Math.ceil(maxValue + buffer)];
  }, [values, referenceLineYValue]);

  return (
    <div className="chart-container">
      {loading ? <Loader className="chart-loader" /> : null}
      <ResponsiveContainer width="98%" height={290}>
        <AreaChart width={730} height={250} data={data} margin={{ top: 0, right: 5, left: -10, bottom: 0 }}>
          <defs>
            <linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#82ca9d" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="yValue" domain={domain} />
          <YAxis yAxisId="xValue" dataKey="xValue" tickFormatter={yTickFormatter} domain={yDomain} />
          <Tooltip content={<CustomTooltip />} />
          {referenceLineYValue ? <ReferenceLine yAxisId="xValue" y={referenceLineYValue} stroke="#3491e3" /> : null}
          <Tooltip />
          <Area
            isAnimationActive={false}
            name={sensorsSignalType}
            yAxisId="xValue"
            type="monotone"
            dataKey="xValue"
            stroke="#82ca9d"
            fillOpacity={1}
            fill="url(#colorPv)"
            dot={<ArtifactLogicDot adaptiveVisibility />}
            activeDot={{
              className: "active-dot",
              onClick: (_, dotInfo: any) => onDotClick?.(dotInfo.payload),
              r: 5,
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default SensorsAreaChart;
